import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getErrorMessage } from "services/api";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Radio } from "components/Radio";
import { Table } from "components/Table";
import { Checkbox } from "components/Checkbox";
import { RegularText, MediumText } from "components/Text";
import { VerticalSpace, HorizontalSpace } from "components/Spacing";

import {
  getAccessList,
  createEmployeesGroup,
  updateEmployeesGroup,
} from "./api";
import styles from "./styles.module.scss";

/** Создание / редактирование группы сотрудников (должности) с правами доступа */
export const EmployeeGroupModal = ({ isEdit, group }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const [name, setName] = useState(isEdit ? (group?.item?.name ?? "") : "");
  const [forOffice, setForOffice] = useState(true);
  const [checkedAccess, setCheckedAccess] = useState(
    isEdit ? (group?.access ?? []).map((a) => a.accessid) : []
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const accessQuery = useQuery(["access-list", forOffice], () =>
    getAccessList(forOffice)
  );
  const accessList = useMemo(
    () => accessQuery.data?.items ?? [],
    [accessQuery.data]
  );

  // При смене типа прав убираем отметки, которых нет в новом списке
  useEffect(() => {
    if (!accessList.length) return;
    setCheckedAccess((prev) =>
      prev.filter((id) => accessList.some((a) => a.accessid === id))
    );
  }, [accessList]);

  const toggleAccess = (accessid) => {
    setCheckedAccess((prev) =>
      prev.includes(accessid)
        ? prev.filter((id) => id !== accessid)
        : [...prev, accessid]
    );
  };

  const colDefs = useMemo(
    () => [
      { field: "accessid", headerName: "№", width: 70 },
      {
        field: "checked",
        headerName: "Доступно",
        width: 100,
        cellRenderer: (params) => (
          <Checkbox checked={Boolean(params.value)} onChange={() => {}} />
        ),
      },
      { field: "name", headerName: "Действие", flex: 1 },
    ],
    []
  );

  const rowData = useMemo(
    () =>
      accessList.map((a) => ({
        accessid: a.accessid,
        name: a.name,
        checked: checkedAccess.includes(a.accessid),
      })),
    [accessList, checkedAccess]
  );

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Укажите название");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateEmployeesGroup({
          usersgroupid: group.item.usersgroupid,
          name: name.trim(),
          accessList: checkedAccess,
        });
      } else {
        await createEmployeesGroup({
          name: name.trim(),
          accessList: checkedAccess,
        });
      }
      queryClient.invalidateQueries(["employees-groups"]);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell width={720}>
      <ModalHeader
        title={isEdit ? group?.item?.name : "Новая группа"}
        onClose={closeModal}
      />
      <ModalBody>
        <div className={styles.groupModalTopRow}>
          <Input
            label="Название"
            labelWidth={90}
            value={name}
            onChange={(ev) => setName(ev.target.value)}
            placeholder="Менеджер по клинингу"
            inputWidth={240}
          />
          {isEdit && (
            <Input
              label="Код"
              labelWidth={40}
              value={group?.item?.usersgroupid ?? ""}
              disabled
              inputWidth={140}
            />
          )}
        </div>
        <VerticalSpace size={16} />
        <div className={styles.accessTypeRow}>
          <MediumText>Права доступа:</MediumText>
          <HorizontalSpace size={16} />
          <Radio
            id="access-office"
            name="access-type"
            label="FloraOffice"
            checked={forOffice}
            onChange={() => setForOffice(true)}
          />
          <HorizontalSpace size={16} />
          <Radio
            id="access-point"
            name="access-type"
            label="FloraPoint"
            checked={!forOffice}
            onChange={() => setForOffice(false)}
          />
        </div>
        <VerticalSpace size={12} />
        <div className={styles.accessButtonsRow}>
          <Button
            onClick={() => setCheckedAccess(accessList.map((a) => a.accessid))}
          >
            Установить все права
          </Button>
          <Button onClick={() => setCheckedAccess([])}>Снять все права</Button>
        </div>
        <VerticalSpace size={12} />
        <Table
          height={340}
          rowData={rowData}
          colDefs={colDefs}
          rowKey="accessid"
          onSelect={(row) => toggleAccess(row.accessid)}
          loading={accessQuery.isLoading}
          error={accessQuery.isError ? getErrorMessage(accessQuery.error) : ""}
          onRetry={accessQuery.refetch}
        />
        {!!error && (
          <>
            <VerticalSpace size={8} />
            <RegularText size={13} color="danger">
              {error}
            </RegularText>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
